"use client"

export type TurnstileWidgetId = string

type TurnstileRenderOptions = {
  sitekey: string
  action: "integration_hub_login"
  execution: "render"
  appearance: "interaction-only"
  theme: "light"
  size: "flexible"
  language: string
  "response-field": false
  retry: "auto"
  "refresh-expired": "auto"
  "refresh-timeout": "auto"
  callback: (token: string) => void
  "error-callback": () => void
  "expired-callback": () => void
  "timeout-callback": () => void
  "unsupported-callback": () => void
  "before-interactive-callback": () => void
  "after-interactive-callback": () => void
}

export type TurnstileApi = {
  ready: (callback: () => void) => void
  render: (
    container: HTMLElement,
    options: TurnstileRenderOptions,
  ) => TurnstileWidgetId
  execute: (widgetId: TurnstileWidgetId) => void
  reset: (widgetId: TurnstileWidgetId) => void
  remove: (widgetId: TurnstileWidgetId) => void
}

declare global {
  interface Window {
    turnstile?: TurnstileApi
  }
}

const SCRIPT_ID = "cloudflare-turnstile-script"
const SCRIPT_URL = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit"

let loaderPromise: Promise<TurnstileApi> | null = null
let cancelPendingLoad: (() => void) | null = null

export function loadTurnstile(): Promise<TurnstileApi> {
  if (loaderPromise) {
    return loaderPromise
  }
  if (window.turnstile?.render) {
    return Promise.resolve(window.turnstile)
  }

  loaderPromise = new Promise<TurnstileApi>((resolve, reject) => {
    let script = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null
    const createdScript = !script

    const cleanup = () => {
      script?.removeEventListener("load", onLoad)
      script?.removeEventListener("error", onError)
      cancelPendingLoad = null
    }
    const onLoad = () => {
      cleanup()
      const api = window.turnstile
      if (!api) {
        script?.remove()
        loaderPromise = null
        reject(new Error("turnstile_api_missing"))
        return
      }
      if (!api.render) {
        script?.remove()
        loaderPromise = null
        reject(new Error("turnstile_api_incomplete"))
        return
      }
      resolve(api)
    }
    const onError = () => {
      cleanup()
      script?.remove()
      loaderPromise = null
      reject(new Error("turnstile_script_failed"))
    }

    if (!script) {
      script = document.createElement("script")
      script.id = SCRIPT_ID
      script.src = SCRIPT_URL
      script.async = true
      script.defer = true
      script.dataset.hubTurnstile = "true"
    }
    script.addEventListener("load", onLoad, { once: true })
    script.addEventListener("error", onError, { once: true })
    cancelPendingLoad = () => {
      cleanup()
      if (!window.turnstile) script?.remove()
      loaderPromise = null
    }
    if (createdScript) {
      document.head.appendChild(script)
    }

    if (window.turnstile?.render) {
      onLoad()
    }
  })

  return loaderPromise
}

export function invalidatePendingTurnstileLoad(): void {
  if (cancelPendingLoad) {
    cancelPendingLoad()
  } else {
    loaderPromise = null
  }
}

export function withTimeout<T>(
  promise: Promise<T>,
  timeoutMillis: number,
  onTimeout?: () => void,
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timeoutId = window.setTimeout(() => {
      onTimeout?.()
      reject(new Error("turnstile_ready_timeout"))
    }, timeoutMillis)

    void promise.then(
      (value) => {
        window.clearTimeout(timeoutId)
        resolve(value)
      },
      (error: unknown) => {
        window.clearTimeout(timeoutId)
        reject(error)
      },
    )
  })
}
